import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import './ProjectSettingsModal.css'

export default function ProjectSettingsModal({ isOpen, onClose, initialTab }) {
  const { project, updateProject } = useProject()
  const { language } = useI18n()
  const isNorwegian = language === 'nb'

  const [activeTab, setActiveTab] = useState(initialTab || 'general')
  const [descriptionMode, setDescriptionMode] = useState('edit')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    timeUnit: 'weeks',
    language: ''
  })
  const [distanceLabels, setDistanceLabels] = useState([])

  useEffect(() => {
    if (project && isOpen) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        startDate: project.startDate || new Date().toISOString().split('T')[0],
        timeUnit: project.timeUnit || 'weeks',
        language: project.language || ''
      })
      const defaultLabels = isNorwegian
        ? ['Start', 'Fase 1', 'Fase 2', 'Fase 3', 'Fullført']
        : ['Start', 'Phase 1', 'Phase 2', 'Phase 3', 'Complete']
      setDistanceLabels(project.distanceLabels?.length > 0 ? [...project.distanceLabels] : defaultLabels)
      setDescriptionMode('edit')
      setActiveTab(initialTab || 'general')
    }
  }, [project, isOpen, isNorwegian])

  if (!isOpen) return null

  const tabs = [
    { id: 'general', label: isNorwegian ? 'Generelt' : 'General' },
    { id: 'description', label: isNorwegian ? 'Beskrivelse' : 'Description' },
    { id: 'axes', label: isNorwegian ? 'Akser' : 'Axes' }
  ]

  const timeUnitOptions = [
    { value: 'weeks', label: isNorwegian ? 'Uker' : 'Weeks' },
    { value: 'days', label: isNorwegian ? 'Dager' : 'Days' },
    { value: 'sprints', label: 'Sprints' },
    { value: 'phases', label: isNorwegian ? 'Faser' : 'Phases' }
  ]

  const languageOptions = [
    { value: '', label: isNorwegian ? 'Automatisk (grensesnittspråk)' : 'Auto (UI language)' },
    { value: 'en', label: 'English' },
    { value: 'nb', label: 'Norsk' }
  ]

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleLabelChange = (index, value) => {
    const newLabels = [...distanceLabels]
    newLabels[index] = value
    setDistanceLabels(newLabels)
  }

  const handleAddLabel = () => {
    setDistanceLabels([...distanceLabels, isNorwegian ? `Fase ${distanceLabels.length}` : `Phase ${distanceLabels.length}`])
  }

  const handleRemoveLabel = (index) => {
    if (distanceLabels.length > 2) {
      setDistanceLabels(distanceLabels.filter((_, i) => i !== index))
    }
  }

  const handleSave = () => {
    updateProject({
      ...formData,
      distanceLabels
    })
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNorwegian ? 'Prosjektinnstillinger' : 'Project Settings'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {activeTab === 'general' && (
            <div className="settings-section">
              <div className="form-group">
                <label>{isNorwegian ? 'Prosjektnavn' : 'Project Name'}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={isNorwegian ? 'Skriv inn prosjektnavn' : 'Enter project name'}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{isNorwegian ? 'Startdato' : 'Start Date'}</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>{isNorwegian ? 'Tidsenhet' : 'Time Unit'}</label>
                  <select
                    value={formData.timeUnit}
                    onChange={(e) => handleInputChange('timeUnit', e.target.value)}
                  >
                    {timeUnitOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'TeDdO-språk' : 'TeDdO Language'}</label>
                <select
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                >
                  {languageOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="form-hint">
                  {isNorwegian
                    ? 'Språket TeDdO svarer på og foreslår oppgaver/flyter i for dette prosjektet.'
                    : 'The language TeDdO replies in and suggests tasks/flows in for this project.'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'description' && (
            <div className="settings-section">
              <div className="description-editor">
                <div className="description-toolbar">
                  <button
                    className={`desc-mode-btn ${descriptionMode === 'edit' ? 'active' : ''}`}
                    onClick={() => setDescriptionMode('edit')}
                  >
                    {isNorwegian ? 'Rediger' : 'Edit'}
                  </button>
                  <button
                    className={`desc-mode-btn ${descriptionMode === 'preview' ? 'active' : ''}`}
                    onClick={() => setDescriptionMode('preview')}
                  >
                    {isNorwegian ? 'Forhåndsvisning' : 'Preview'}
                  </button>
                </div>
                {descriptionMode === 'edit' ? (
                  <textarea
                    className="description-textarea"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={isNorwegian ? 'Beskriv prosjektet med Markdown...' : 'Describe the project using Markdown...'}
                    rows={12}
                  />
                ) : (
                  <div className="description-preview">
                    {formData.description ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {formData.description}
                      </ReactMarkdown>
                    ) : (
                      <p className="preview-empty">
                        {isNorwegian ? 'Ingen beskrivelse ennå.' : 'No description yet.'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'axes' && (
            <div className="settings-section">
              <div className="form-group">
                <label>{isNorwegian ? 'X-akse (Tid)' : 'X-axis (Time)'}</label>
                <p className="form-hint">
                  {isNorwegian
                    ? 'Tidsenhet defineres under Generelt-fanen.'
                    : 'Time unit is defined in the General tab.'}
                </p>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'Y-akse etiketter (Avstand/Faser)' : 'Y-axis Labels (Distance/Phases)'}</label>
                <p className="form-description">
                  {isNorwegian
                    ? 'Definer etiketter for Y-aksen. Legg til, fjern eller endre rekkefølgen.'
                    : 'Define labels for the Y-axis. Add, remove, or reorder.'}
                </p>
                <div className="labels-list">
                  {distanceLabels.map((label, index) => (
                    <div key={index} className="label-row">
                      <span className="label-index">{index}</span>
                      <input
                        type="text"
                        value={label}
                        onChange={(e) => handleLabelChange(index, e.target.value)}
                        className="label-input"
                      />
                      <button
                        className="btn-icon danger"
                        onClick={() => handleRemoveLabel(index)}
                        disabled={distanceLabels.length <= 2}
                        title={isNorwegian ? 'Fjern' : 'Remove'}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button className="btn btn-secondary add-btn" onClick={handleAddLabel}>
                  + {isNorwegian ? 'Legg til etikett' : 'Add label'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {isNorwegian ? 'Avbryt' : 'Cancel'}
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            {isNorwegian ? 'Lagre' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
